import { CSSProperties, useState } from 'react';

import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Collapse } from '@components/animators/collapse';
import { Rotate } from '@components/animators/Rotate';
import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconDepartment } from '@components/icons/department';
import { IconDirectionRight } from '@components/icons/direction-right';
import { IconDrag } from '@components/icons/drag';
import { IconJob } from '@components/icons/job';
import { IconPinned } from '@components/icons/pinned';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';

import { DepartmentOrderingData } from 'api/departments/requests';
import { Spacer } from 'components/Spacer';
import { useStore } from 'models/Provider';

import { RenderJobs } from './job-order-list';
import { EmptyCard, PinButton } from './ordering';

type DraggableDepartmentProps = {
  department: DepartmentOrderingData;
  unpin: (departmentId: number) => void;
  handlePinDepartment?: (departmentId: number) => void;
  isPinned?: boolean;
  isDragging?: boolean;
};

export const DraggableDepartment = ({
  department,
  unpin,
  handlePinDepartment,
  isPinned = false,
  isDragging = false,
}: DraggableDepartmentProps) => {
  const { t } = useStore();
  const [expandIndex, setExpandIndex] = useState<null | number>(null);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: String(department.id),
    data: {
      order: department.order,
    },
  });

  let style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
  };

  if (isDragging) {
    style.opacity = 0.65;
    if (expandIndex !== null) {
      setExpandIndex(null);
    }
  }

  if (isPinned) {
    style.cursor = 'grab';
  }

  const toggleExpand = (id: number) => {
    if (expandIndex === null) {
      setExpandIndex(id);
    } else {
      setExpandIndex(null);
    }
  };

  const onUnpinClick = (departmentId: number) => {
    setExpandIndex(null);
    unpin(departmentId);
  };
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <Card space={4} variant="gray">
        <Stack space={2} orientation="horizontal" align="center" justify="space-between">
          <Stack space={2} orientation="horizontal" align="center">
            {isPinned ? (
              <>
                <Spacer top={10} bottom={10} left={10} right={10} />
                <IconDrag color="purple50" />
                <PinButton
                  tooltip={t('company.settings.joborder.departments.unpin.hover')}
                  onClick={() => onUnpinClick(department.id)}
                />
              </>
            ) : (
              <PinButton
                isAnimated
                tooltip={t('company.settings.joborder.departments.pin.hover')}
                onClick={() => handlePinDepartment?.(department.id)}
              />
            )}
            <Stack space={2} orientation="horizontal" align="center" justify="flex-start">
              <IconDepartment color="purple50" />
              <Text font="semibold14" color="purple100" text={department.title} />
            </Stack>
          </Stack>
          <IconButton
            icon={
              <Rotate isRotated={expandIndex === department.id}>
                <IconDirectionRight color="purple50" />
              </Rotate>
            }
            variant="ghost-purple"
            onClick={() => toggleExpand(department.id)}
          />
        </Stack>
        <Collapse isCollapsed={department.id !== expandIndex}>
          <Stack space={4} style={{ cursor: 'default' }}>
            {department.jobs.length === 0 ? (
              <>
                <Spacer top={2} />
                <EmptyCard
                  text={t('company.settings.joborder.departments.jobs.none')}
                  icon={<IconJob color="purple80" />}
                  space={4}
                />
              </>
            ) : (
              <>
                <Spacer top={4} />
                <RenderJobs jobs={department.jobs} enterprise />
              </>
            )}
          </Stack>
        </Collapse>
      </Card>
    </div>
  );
};

export const DepartmentCard = (props: { department: DepartmentOrderingData }) => {
  const { department } = props;
  const { setNodeRef } = useDroppable({
    id: String(department.id),
    data: {
      order: department.order,
    },
  });

  return (
    <div ref={setNodeRef}>
      <Card space={4} style={{ background: theme.color.purple10 }} interactive>
        <Stack space={2} orientation="horizontal" align="center" justify="space-between">
          <Stack space={2} orientation="horizontal" align="center">
            <IconDrag color="purple50" />
            <IconButton icon={<IconPinned color="blue100" />} variant="ghost-purple" />
            <Stack space={2} orientation="horizontal" align="center" justify="flex-start">
              <IconDepartment color="purple50" />
              <Text font="semibold14" color="purple100" text={department.title} />
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </div>
  );
};
